import * as Sentry from '@sentry/react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router';
import React, { useEffect, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { getAuth } from 'firebase/auth';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { ToastContainer } from './components/ToastContainer';
import { SystemProvider, useSystem } from './contexts/SystemContext';
import { FeatureGatingProvider } from './contexts/FeatureGatingContext';
import { AccountProvider } from './contexts/AccountContext';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Onboarding from './pages/Onboarding';
import IpProtectionAgreement from './pages/IpProtectionAgreement';
import JoinWorkspace from './pages/JoinWorkspace';
import HowItWorks from './pages/HowItWorks';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import PublicStatutes from './pages/PublicStatutes';
import PublicStatute from './pages/PublicStatute';
import NotFound from './pages/NotFound';
import AdminImpersonationBanner from './components/admin/AdminImpersonationBanner';
import DomainGuard from './components/DomainGuard';
import PageViewTracker from './components/PageViewTracker';
import MarketingRouteSEO from './components/MarketingRouteSEO';
import { isAppHost, isLocalhost, isRefreshExemptPath } from './lib/domains';

/** Admin panel — separate JS chunk; fetched only when an admin visits /admin */
const AdminRoutesModule = lazy(() => import('./routes/AdminRoutesModule'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Cases = lazy(() => import('./pages/Cases'));
const Diary = lazy(() => import('./pages/Diary'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const Library = lazy(() => import('./pages/Library'));
const TeamSettings = lazy(() => import('./pages/TeamSettings'));
const TeamMemberProfile = lazy(() => import('./pages/TeamMemberProfile'));
const Drafts = lazy(() => import('./pages/Drafts'));
const Uploads = lazy(() => import('./pages/Uploads'));
const ImportOldCases = lazy(() => import('./pages/ImportOldCases'));
const RecycleBin = lazy(() => import('./pages/RecycleBin'));
const Pricing = lazy(() => import('./pages/Pricing'));

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[50vh] w-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800" />
    </div>
  );
}

function AdminLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

import { WorkspaceAccessProvider } from './contexts/WorkspaceAccessContext';
import { useWorkspaceTabs } from './store/workspaceTabsStore';
import { hasAcceptedIpProtectionAgreement } from './lib/legalAcceptance';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, userData, loading, isAdmin, activeWorkspaceId } = useAuth();
  const { config, loading: systemLoading } = useSystem();
  const location = useLocation();
  
  if (loading || systemLoading) return <div className="flex h-screen items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (userData && !userData.onboarded && location.pathname !== '/onboarding' && !location.pathname.startsWith('/join/')) {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    userData?.onboarded &&
    !hasAcceptedIpProtectionAgreement(userData) &&
    location.pathname !== '/ip-protection-agreement' &&
    location.pathname !== '/intellectual-property-notice' &&
    location.pathname !== '/acknowledgement' &&
    location.pathname !== '/affidavit' &&
    !location.pathname.startsWith('/join/')
  ) {
    return <Navigate to="/ip-protection-agreement" replace />;
  }

  // If user is onboarded but somehow has no active workspace, redirect to onboarding to create one
  // unless they are currently on the onboarding or join page
  const isAdminPath =
    location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  if (
    userData &&
    userData.onboarded &&
    !activeWorkspaceId &&
    location.pathname !== '/onboarding' &&
    !location.pathname.startsWith('/join/') &&
    !isAdminPath
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  const accountStatus = userData?.systemStatus as string | undefined;
  const isBlockedAccount =
    !isAdmin &&
    !isAdminPath &&
    (accountStatus === 'suspended' || accountStatus === 'banned');

  if (isBlockedAccount) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold text-text-main mb-2">
          {accountStatus === 'banned' ? 'Account banned' : 'Account suspended'}
        </h1>
        <p className="text-text-muted max-w-md">
          {accountStatus === 'banned'
            ? 'Your access to LexGat has been permanently revoked. Contact support if you believe this is an error.'
            : 'Your account is temporarily suspended. Please contact LexGat support to restore access.'}
        </p>
        <button
          onClick={() => {
            getAuth().signOut();
          }}
          className="mt-8 px-4 py-2 border border-border-light rounded-lg text-sm font-medium hover:bg-bg-secondary transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (config.maintenanceMode && !isAdmin) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-6 text-center">
        <div className="bg-rose-50 text-rose-500 p-4 rounded-full mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">System Maintenance</h1>
        <p className="text-slate-500 max-w-md">
          The platform is currently down for scheduled maintenance. Our team is working to restore access as soon as possible. Please check back later.
        </p>
        <button 
          onClick={() => {
            getAuth().signOut();
          }}
          className="mt-8 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
        >
          Sign out
        </button>
      </div>
    );
  }
  
  return <>{children}</>;
}

function SessionRestorer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Session restoration is an app-domain concern only. On the marketing domain
    // (lexgat.com), public statute browsing and landing pages should never get
    // bounced to /dashboard on a refresh.
    if (!isAppHost() && !isLocalhost()) return;

    const isRefresh = sessionStorage.getItem('lexgat_session_active');

    if (!isRefresh) {
      sessionStorage.setItem('lexgat_session_active', '1');
      const state = useWorkspaceTabs.getState();
      const lastSessionActiveAt = state.lastSessionActiveAt;
      const isRecent = lastSessionActiveAt && (Date.now() - lastSessionActiveAt <= 5 * 60 * 1000);

      if (!isRecent) {
        useWorkspaceTabs.getState().setActiveTab(null);
      }
    } else {
      // It's a refresh. Open on Home screen by default (while keeping tabs)
      useWorkspaceTabs.getState().setActiveTab(null);
      // Paths that should refresh in place rather than bouncing to /dashboard.
      if (!isRefreshExemptPath(pathname)) {
        navigate('/dashboard');
      }
    }
  }, []); // Run once on mount

  useEffect(() => {
    const interval = setInterval(() => {
      useWorkspaceTabs.getState().updateLastActive();
    }, 10000);
    
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        useWorkspaceTabs.getState().updateLastActive();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 px-6 text-center">
          <h1 className="text-2xl font-bold text-zinc-900 mb-3">Something went wrong</h1>
          <p className="text-zinc-600 mb-6 max-w-md">
            We&apos;ve been notified and are looking into it. Please try refreshing the page.
          </p>
          <button
            type="button"
            onClick={resetError}
            className="bg-zinc-900 text-white px-6 py-3 rounded-full font-medium hover:bg-zinc-800 transition-all"
          >
            Try again
          </button>
        </div>
      )}
      showDialog={false}
    >
    <HelmetProvider>
    <SystemProvider>
    <AuthProvider>
      <WorkspaceAccessProvider>
      <AppearanceProvider>
      <ToastProvider>
        <BrowserRouter>
          <DomainGuard>
          <SessionRestorer />
          <AdminImpersonationBanner />
          <ScrollToTop />
          <PageViewTracker />
          <MarketingRouteSEO />
          <SentryRoutes>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/pricing"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Pricing />
                </Suspense>
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/statutes" element={<PublicStatutes />} />
            <Route path="/statutes/:id" element={<PublicStatute />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/ip-protection-agreement" element={<ProtectedRoute><IpProtectionAgreement /></ProtectedRoute>} />
            <Route path="/intellectual-property-notice" element={<Navigate to="/ip-protection-agreement" replace />} />
            <Route path="/acknowledgement" element={<Navigate to="/ip-protection-agreement" replace />} />
            <Route path="/affidavit" element={<Navigate to="/ip-protection-agreement" replace />} />
            <Route path="/complete-profile" element={<ProtectedRoute><Onboarding phase="complete" /></ProtectedRoute>} />
            <Route path="/join/:workspaceId/:inviteId" element={<ProtectedRoute><JoinWorkspace /></ProtectedRoute>} />
            
            <Route element={<ProtectedRoute><AccountProvider><FeatureGatingProvider><Layout /></FeatureGatingProvider></AccountProvider></ProtectedRoute>}>
              <Route path="/dashboard" element={<Suspense fallback={<RouteLoadingFallback />}><Dashboard /></Suspense>} />
              <Route path="/cases" element={<Suspense fallback={<RouteLoadingFallback />}><Cases /></Suspense>} />
              <Route path="/closed-cases" element={<Suspense fallback={<RouteLoadingFallback />}><Cases closedOnly={true} /></Suspense>} />
              <Route path="/diary" element={<Suspense fallback={<RouteLoadingFallback />}><Diary /></Suspense>} />
              <Route path="/library" element={<Suspense fallback={<RouteLoadingFallback />}><Library /></Suspense>} />
              <Route path="/library/:id" element={<Suspense fallback={<RouteLoadingFallback />}><Library /></Suspense>} />
              <Route path="/drafts" element={<Suspense fallback={<RouteLoadingFallback />}><Drafts /></Suspense>} />
              <Route path="/uploads" element={<Suspense fallback={<RouteLoadingFallback />}><Uploads /></Suspense>} />
              <Route path="/import-old-cases" element={<Suspense fallback={<RouteLoadingFallback />}><ImportOldCases /></Suspense>} />
              <Route path="/recycle-bin" element={<Suspense fallback={<RouteLoadingFallback />}><RecycleBin /></Suspense>} />
              <Route path="/drafts/:id" element={<></>} />
              <Route path="/drafts/system/:type" element={<></>} />
              <Route path="/analytics" element={<Suspense fallback={<RouteLoadingFallback />}><Analytics /></Suspense>} />
              <Route path="/cases/:caseId" element={<></>} />
              <Route path="/workspace/new" element={<></>} />
              <Route path="/settings" element={<Suspense fallback={<RouteLoadingFallback />}><Settings /></Suspense>} />
              <Route path="/team" element={<Suspense fallback={<RouteLoadingFallback />}><TeamSettingsWrapper /></Suspense>} />
              <Route path="/team/:memberId" element={<Suspense fallback={<RouteLoadingFallback />}><TeamMemberProfile /></Suspense>} />
            </Route>

            {/* Admin — lazy chunk; only downloaded when route is visited */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <Suspense fallback={<AdminLoadingFallback />}>
                    <AdminRoutesModule />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
          </DomainGuard>
          <ToastContainer />
        </BrowserRouter>
      </ToastProvider>
      </AppearanceProvider>
      </WorkspaceAccessProvider>
    </AuthProvider>
    </SystemProvider>
    </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

function TeamSettingsWrapper() {
  const { activeWorkspaceId } = useAuth();
  return <TeamSettings activeWorkspaceId={activeWorkspaceId} />;
}
